if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Subscribe to variant changes for image filtering (must run regardless of thumbnails)
        if (window.subscribe) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }

        // Run initial filter on page load using selected variant options
        const runInitialFilter = () => {
          const productContainer = this.closest('.product') || document;
          const picker = productContainer.querySelector('variant-selects, variant-radios') || productContainer;
          const initialOptions = [];
          picker.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(input => {
            if (!input.closest('noscript')) initialOptions.push(input.value);
          });
          picker.querySelectorAll('.product-form__input select.select__select').forEach(select => {
            if (!select.closest('noscript')) initialOptions.push(select.value);
          });

          if (initialOptions.length > 0) {
            this.filterMediaByOptions(initialOptions);
          } else if (this.elements.viewer) {
            const defaultActive = this.elements.viewer.querySelector('[data-media-id].is-active') ||
                                  this.elements.viewer.querySelector('[data-media-id]');
            if (defaultActive && defaultActive.dataset.mediaId) {
              this.setActiveMedia(defaultActive.dataset.mediaId, false);
            }
          }
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', runInitialFilter);
        } else {
          setTimeout(runInitialFilter, 0);
        }

        // Listen for direct input/select changes in variant pickers
        const productContainer = this.closest('.product') || document;
        productContainer.addEventListener('change', (e) => {
          if (e.target && (e.target.matches('input[type="radio"]') || e.target.matches('select.select__select'))) {
            setTimeout(runInitialFilter, 0);
          }
        });

        // Thumbnail-specific setup (only if thumbnails exist)
        if (!this.elements.thumbnails) return;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        const targetThumbnails = this.elements.thumbnails.querySelectorAll('[data-target]');
        targetThumbnails.forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, true);
            });
          }
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      filterMediaByOptions(options, featuredMediaId = null) {
        if (!options || options.length === 0) return;

        // Reset scroll position of main gallery viewer and thumbnails immediately
        if (this.elements.viewer && this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollLeft = 0;
        }
        if (this.elements.thumbnails && this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollLeft = 0;
        }

        const isFeaturedMedia = (mediaId) => {
          if (!featuredMediaId || !mediaId) return false;
          return mediaId === featuredMediaId || mediaId.endsWith(featuredMediaId) || featuredMediaId.endsWith(mediaId);
        };

        // 1. Filter Viewer items (Main Product Slides)
        let targetMediaId = null;

        // Priority 1: Explicit featuredMediaId for selected variant
        if (featuredMediaId && this.elements.viewer) {
          const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${featuredMediaId}"]`) ||
                              this.elements.viewer.querySelector(`[data-media-id$="${featuredMediaId}"]`);
          if (targetMedia) {
            targetMediaId = targetMedia.dataset.mediaId;
          }
        }

        if (this.elements.viewer) {
          const viewerItems = this.elements.viewer.querySelectorAll('[data-media-id]');
          let highestScore = -1;
          let highestScoreMediaId = null;
          let firstNonOtherMediaId = null;

          viewerItems.forEach(item => {
            const group = item.dataset.variantGroup;
            const mediaId = item.dataset.mediaId;
            const explicitOther = this.isMediaExplicitlyForOtherVariant(group, mediaId, options);

            if (isFeaturedMedia(mediaId)) {
              item.dataset.variantMatching = 'true';
              if (!targetMediaId) targetMediaId = mediaId;
            } else if (!explicitOther) {
              item.dataset.variantMatching = 'true';
              if (!firstNonOtherMediaId && mediaId) {
                firstNonOtherMediaId = mediaId;
              }
              const score = this.getOptionMatchCount(group, options);
              if (score > highestScore) {
                highestScore = score;
                highestScoreMediaId = mediaId;
              }
            } else {
              item.dataset.variantMatching = 'false';
            }
          });

          // Priority 2: Highest matching alt-text score image (explicit flavor match)
          if (!targetMediaId && highestScore > 0 && highestScoreMediaId) {
            targetMediaId = highestScoreMediaId;
          }

          // Priority 3: First image that isn't explicitly for another variant
          if (!targetMediaId && firstNonOtherMediaId) {
            targetMediaId = firstNonOtherMediaId;
          }

          // Priority 4: Absolute fallback (first viewer item)
          if (!targetMediaId && viewerItems.length > 0) {
            targetMediaId = viewerItems[0].dataset.mediaId;
          }

          if (targetMediaId) {
            this.setActiveMedia(targetMediaId, false);
          }
        }

        // 2. Filter Thumbnails
        if (this.elements.thumbnails) {
          const thumbItems = this.elements.thumbnails.querySelectorAll('[data-target]');
          thumbItems.forEach(item => {
            const group = item.dataset.variantGroup;
            const targetId = item.dataset.target;

            let isVisible = false;
            if (isFeaturedMedia(targetId)) {
              isVisible = true;
            } else {
              const explicitOther = this.isMediaExplicitlyForOtherVariant(group, targetId, options);
              if (!explicitOther) {
                isVisible = true;
              }
            }

            if (targetMediaId && (targetId === targetMediaId || targetId.endsWith(targetMediaId))) {
              isVisible = true;
            }

            if (isVisible) {
              item.style.display = '';
            } else {
              item.style.display = 'none';
            }
          });
        }

        if (this.elements.viewer && typeof this.elements.viewer.initPages === 'function') this.elements.viewer.initPages();
        if (this.elements.thumbnails && typeof this.elements.thumbnails.initPages === 'function') this.elements.thumbnails.initPages();
      }

      onVariantChange(event) {
        const variant = event.data.variant;
        if (!variant) return;

        const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        const featuredMedia = variant.featured_media || variant.featured_image;
        const featuredMediaId = featuredMedia ? `${this.dataset.section || ''}-${featuredMedia.id}` : null;
        
        this.filterMediaByOptions(options, featuredMediaId);
      }

      onSlideChanged(event) {
        if (!event || !event.detail || !event.detail.currentElement || !event.detail.currentElement.dataset) return;
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend = false) {
        if (!this.elements.viewer) return;
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia && mediaId) {
          activeMedia = this.elements.viewer.querySelector(`[data-media-id$="${mediaId}"]`);
        }
        if (!activeMedia) {
          activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active') ||
                        this.elements.viewer.querySelector('[data-media-id]');
        }
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          element.style.setProperty('display', 'none', 'important');
        });
        activeMedia.classList.add('is-active');
        activeMedia.style.setProperty('display', 'block', 'important');

        // Force image inside active media to load and render immediately
        const activeImg = activeMedia.querySelector('img');
        if (activeImg) {
          activeImg.removeAttribute('loading');
          const srcset = activeImg.getAttribute('srcset');
          if (srcset) activeImg.setAttribute('srcset', srcset);
          const src = activeImg.getAttribute('src');
          if (src) activeImg.src = src;
        }

        // Reset scroll position of gallery slider container to 0
        const sliderList = this.elements.viewer.querySelector('[id^="Slider-Gallery"]') || activeMedia.parentElement;
        if (sliderList) {
          sliderList.scrollLeft = 0;
        }
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollLeft = 0;
        }

        if (prepend) {
          if (this.elements.viewer.slider) {
            this.elements.viewer.slider.scrollLeft = 0;
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        let activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail && activeMedia.dataset.mediaId) {
          activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        }
        if (!activeThumbnail) {
          activeThumbnail = this.elements.thumbnails.querySelector('[data-target]');
        }
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');
        thumbnail.style.display = '';

        if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      getOptionGroups() {
        const productContainer = this.closest('.product') || document;
        const picker = productContainer.querySelector('variant-selects, variant-radios') || productContainer;
        const groups = [];

        picker.querySelectorAll('.product-form__input, fieldset.js').forEach(container => {
          if (container.closest('noscript')) return;
          const values = [];
          container.querySelectorAll('input[type="radio"], select option').forEach(input => {
            let val = (input.value || '').trim();
            if (val) {
              val = val.replace(/sold out|unavailable|in stock/gi, '').trim();
              if (val && !values.includes(val)) {
                values.push(val);
              }
            }
          });
          if (values.length > 0) {
            groups.push({ values });
          }
        });

        return groups;
      }

      getAllVariantOptions() {
        const groups = this.getOptionGroups();
        const all = [];
        groups.forEach(g => g.values.forEach(v => {
          if (!all.includes(v)) all.push(v);
        }));
        return all;
      }

      isOptionValueMatching(valA, valB) {
        if (!valA || !valB) return false;
        const a = this.normalizeText(valA);
        const b = this.normalizeText(valB);
        if (a === b || a.includes(b) || b.includes(a)) return true;

        const noise = new Set(['and', 'or', 'of', 'with', 'the', 'a', 'an', 'in', 'for', 'flavor', 'flavour', 'lbs', 'lb', 'serving', 'servings', 'pack', 'packs', 'capsule', 'capsules', 'scoop', 'scoops', 'gm', 'g', 'ml', 'oz']);
        const isMeaningfulWord = (w) => (w.length >= 2 || /^\d+$/.test(w)) && !noise.has(w);

        const aWords = (a.match(/[a-z0-9]+/g) || []).filter(isMeaningfulWord);
        const bWords = (b.match(/[a-z0-9]+/g) || []).filter(isMeaningfulWord);

        if (aWords.length === 0 || bWords.length === 0) return false;

        return aWords.every(aW => bWords.includes(aW)) || bWords.every(bW => aWords.includes(bW));
      }

      getVariantMediaMap(options = null) {
        const productContainer = this.closest('.product') || document;
        const picker = productContainer.querySelector('variant-selects, variant-radios') || productContainer;
        const script = picker ? picker.querySelector('script[type="application/json"]') : null;
        if (!script) return { currentFeaturedMediaId: null, otherFeaturedMediaIds: new Set() };

        try {
          const variants = JSON.parse(script.textContent);
          const otherFeaturedMediaIds = new Set();
          let currentFeaturedMediaId = null;
          let sameFlavorFeaturedMediaId = null;

          let selectedOptions = options ? options.map(o => this.normalizeText(o)) : [];
          if (selectedOptions.length === 0) {
            picker.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(input => {
              if (!input.closest('noscript')) selectedOptions.push(this.normalizeText(input.value));
            });
            picker.querySelectorAll('.product-form__input select.select__select').forEach(select => {
              if (!select.closest('noscript')) selectedOptions.push(this.normalizeText(select.value));
            });
          }

          const selectedFlavor = selectedOptions.length > 0 ? selectedOptions[0] : null;

          variants.forEach(variant => {
            const featMedia = variant.featured_media || variant.featured_image;
            if (!featMedia || !featMedia.id) return;

            const mediaIdStr = String(featMedia.id);
            const varOptions = (variant.options || [variant.option1, variant.option2, variant.option3]).filter(Boolean);
            const varFlavor = varOptions.length > 0 ? varOptions[0] : null;

            const isSelectedVariant = selectedOptions.length > 0 && selectedOptions.every(sel => {
              return varOptions.some(varOpt => this.isOptionValueMatching(sel, varOpt));
            });

            const isSameFlavor = selectedFlavor && varFlavor && this.isOptionValueMatching(selectedFlavor, varFlavor);

            if (isSelectedVariant) {
              currentFeaturedMediaId = mediaIdStr;
            } else if (isSameFlavor) {
              if (!sameFlavorFeaturedMediaId) sameFlavorFeaturedMediaId = mediaIdStr;
            } else {
              otherFeaturedMediaIds.add(mediaIdStr);
            }
          });

          return { currentFeaturedMediaId: currentFeaturedMediaId || sameFlavorFeaturedMediaId, otherFeaturedMediaIds };
        } catch (e) {
          return { currentFeaturedMediaId: null, otherFeaturedMediaIds: new Set() };
        }
      }

      normalizeText(str) {
        let s = (str || '').toString().toLowerCase().trim();
        s = s.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"');
        s = s.replace(/sold out|unavailable|in stock/gi, '').trim();
        s = s.replace(/[-_]/g, ' ');
        s = s.replace(/(\d+)\s*lbs?/gi, '$1lbs');
        s = s.replace(/(\d+)\s*kg/gi, '$1kg');
        s = s.replace(/cocunut|cocnut|coconat/gi, 'coconut');
        s = s.replace(/choclate|chocolat/gi, 'chocolate');
        s = s.replace(/vanila|vanilaa/gi, 'vanilla');
        s = s.replace(/gauva/gi, 'guava');
        s = s.replace(/haselnut/gi, 'hazelnut');
        return s;
      }

      getOptionMatchCount(group, options) {
        if (!group || group.trim() === '') return 0;
        let score = 0;
        const groupLower = this.normalizeText(group);
        const noise = new Set(['and', 'or', 'of', 'with', 'the', 'a', 'an', 'in', 'for', 'flavor', 'flavour', 'lbs', 'lb', 'serving', 'servings', 'pack', 'packs', 'capsule', 'capsules', 'gm', 'g', 'ml', 'oz']);

        options.forEach(opt => {
          const optLower = this.normalizeText(opt);
          if (!optLower) return;

          if (this.isOptionValueMatching(groupLower, optLower)) {
            score += 10;
          } else {
            const gWords = groupLower.match(/[a-z0-9]+/g) || [];
            const oWords = (optLower.match(/[a-z0-9]+/g) || []).filter(w => (w.length >= 2 || /^\d+$/.test(w)) && !noise.has(w));
            if (oWords.length > 0 && oWords.every(oW => gWords.includes(oW))) {
              score += 5;
            }
          }
        });
        return score;
      }

      isMediaExplicitlyForOtherVariant(group, mediaId, options) {
        // Condition 1: Check Shopify variant object featured_media / featured_image associations
        const { currentFeaturedMediaId, otherFeaturedMediaIds } = this.getVariantMediaMap(options);
        if (mediaId && otherFeaturedMediaIds.size > 0) {
          const mediaIdStr = String(mediaId);
          for (const otherId of otherFeaturedMediaIds) {
            if (mediaIdStr === otherId || mediaIdStr.endsWith(otherId)) {
              if (!currentFeaturedMediaId || (!mediaIdStr.endsWith(currentFeaturedMediaId) && currentFeaturedMediaId !== otherId)) {
                return true;
              }
            }
          }
        }

        if (!group || group.trim() === '') return false;
        const groupLower = this.normalizeText(group);
        
        const productTitle = this.dataset.productTitle ? this.normalizeText(this.dataset.productTitle) : '';
        if (groupLower === productTitle) return false;

        const selectedLower = options.map(opt => this.normalizeText(opt)).filter(Boolean);
        if (selectedLower.length === 0) return false;

        const optionGroups = this.getOptionGroups();
        if (optionGroups.length === 0) return false;

        // Condition 2: Check Positional Alt Text ("Flavour 1", "Flavour 2", "Flavour 3", "Flavor 1", etc.)
        const flavMatch = groupLower.match(/(?:flavour|flavor|option)\s*(\d+)/i);
        if (flavMatch && flavMatch[1]) {
          const altPos = parseInt(flavMatch[1], 10);
          const mainOptGroup = optionGroups[0];
          if (mainOptGroup && mainOptGroup.values.length > 0) {
            const selectedVal = selectedLower[0];
            const selectedPos = mainOptGroup.values.findIndex(v => {
              return this.isOptionValueMatching(selectedVal, v);
            }) + 1;

            if (selectedPos > 0 && altPos !== selectedPos) {
              return true;
            }
          }
        }

        // Condition 3: Check Explicit Option Value Names in Alt Text
        for (const optGroup of optionGroups) {
          const mentionedValues = optGroup.values.filter(val => {
            return this.isOptionValueMatching(groupLower, val);
          });

          if (mentionedValues.length > 0) {
            const matchesCurrentSelection = mentionedValues.some(mVal => {
              return selectedLower.some(sel => this.isOptionValueMatching(sel, mVal));
            });

            if (!matchesCurrentSelection) {
              return true; // Explicitly mentions another variant option value that is NOT currently selected
            }
          }
        }

        return false;
      }

      // Universal variant group matching based on Alt text and selected variant options
      matchesVariantGroup(group, options) {
        if (!group || group.trim() === '') return true;
        const groupLower = this.normalizeText(group);
        
        const productTitle = this.dataset.productTitle ? this.normalizeText(this.dataset.productTitle) : '';
        if (groupLower === productTitle) return true;

        if (this.isMediaExplicitlyForOtherVariant(group, null, options)) {
          return false;
        }

        const selectedLower = options.map(opt => this.normalizeText(opt)).filter(Boolean);
        if (selectedLower.length === 0) return true;

        const noise = new Set(['and', 'or', 'of', 'with', 'the', 'a', 'an', 'in', 'for', 'flavor', 'flavour', 'lbs', 'lb', 'serving', 'servings', 'pack', 'packs', 'capsule', 'capsules', 'scoop', 'scoops', 'gm', 'g', 'ml', 'oz']);
        const isMeaningfulWord = (w) => (w.length >= 2 || /^\d+$/.test(w)) && !noise.has(w);

        return selectedLower.some(sel => {
          if (!sel) return false;
          const selLower = this.normalizeText(sel);
          if (groupLower.includes(selLower) || selLower.includes(groupLower)) return true;
          const gWords = (groupLower.match(/[a-z0-9]+/g) || []).filter(isMeaningfulWord);
          const sWords = (selLower.match(/[a-z0-9]+/g) || []).filter(isMeaningfulWord);
          return sWords.length > 0 && sWords.every(sW => gWords.includes(sW));
        });
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
